import type {
  AdvertiserResource,
  AdvertiserUserCampaignResource,
  CampaignResource,
  CampaignStatus,
  OfferType,
  TrafficSourceCostResource,
} from '@metapic-apps/shared'
import type { DeepReadonly } from 'vue'

import { normalizeMoney } from '@/lib/adapters/common.adapter'
import { round } from '@/lib/helpers'
import { CampaignTypeEnum } from '@/lib/types/campaign.types'
import { type CampaignListItemType, CampaignStatusEnum } from '@/lib/types/campaigns.types'
import type { ProcessableImageType } from '@/lib/types/common.types'
import type { TrafficSourceCostsType } from '@/lib/types/traffic-source-costs.types'

export type CreatorAdvertiser = {
  id: number
  name: string
  currency: string
  logo: ProcessableImageType
  revenueText?: string
  campaign?: CampaignListItemType
  trafficSourceCosts: TrafficSourceCostsType[]
}

const campaignTypeMap: Record<OfferType, CampaignTypeEnum> = {
  standard: CampaignTypeEnum.Standard,
  user_accept: CampaignTypeEnum.UserAccepts,
  store_accept: CampaignTypeEnum.StoreAccepts,
  suggestion: CampaignTypeEnum.Suggestion,
}

const campaignStatusMap: Record<CampaignStatus, CampaignStatusEnum> = {
  active: CampaignStatusEnum.Active,
  deleted: CampaignStatusEnum.Ended,
  ended: CampaignStatusEnum.Ended,
  paused: CampaignStatusEnum.Paused,
  scheduled: CampaignStatusEnum.Scheduled,
}

const normalizeImage = (image: DeepReadonly<AdvertiserResource['logo']>): ProcessableImageType => ({
  key: image.key,
  baseUrl: image.base_url,
  url: image.url,
})

const normalizeTrafficSourceCost = (
  cost: DeepReadonly<TrafficSourceCostResource>,
  advertiserCurrency: string,
): TrafficSourceCostsType => {
  const cpc = normalizeMoney(cost.cpc_v2 ?? { amount: '0', currency: advertiserCurrency })
  const invoiceCpc = cost.invoice_cpc_v2 ? normalizeMoney(cost.invoice_cpc_v2) : null

  return {
    source: cost.source,
    ...(cost.source_name && { sourceName: cost.source_name }),
    cpc,
    cpa: cost.cpa,
    rateCpa: cost.cpa ? round(cost.cpa * 100, 2) : null,
    invoiceCpc,
    invoiceCpa: cost.invoice_cpa ?? null,
    invoiceRateCpc: invoiceCpc?.amount ?? null,
    invoiceRateCpa: cost.invoice_cpa ? round(cost.invoice_cpa * 100, 2) : null,
  }
}

const normalizeCampaign = (
  campaign: DeepReadonly<CampaignResource>,
  advertiser: DeepReadonly<AdvertiserResource>,
  trafficSourceCosts: TrafficSourceCostsType[],
): CampaignListItemType => {
  const internalName = campaign.internal_name ?? campaign.title ?? `Campaign #${campaign.id}`

  return {
    id: campaign.id,
    type: campaignTypeMap[campaign.type],
    status: campaignStatusMap[campaign.status],
    internalName,
    advertiserId: advertiser.id,
    advertiserName: advertiser.name,
    advertiserCurrency: advertiser.currency,
    advertiserLogo: normalizeImage(advertiser.logo),
    trafficSourceCosts,
    ...(campaign.constraints.current_clicks && {
      currentClicks: campaign.constraints.current_clicks,
    }),
    ...(campaign.constraints.max_clicks && {
      maxClicks: campaign.constraints.max_clicks,
    }),
    perUserLimit: campaign.constraints.per_user_limit,
    ...(campaign.constraints.valid_from && { validFrom: campaign.constraints.valid_from }),
    ...(campaign.constraints.valid_until && { validUntil: campaign.constraints.valid_until }),
    ...(campaign.image && { image: normalizeImage(campaign.image) }),
  }
}

const normalizeCreatorAdvertiser = ({
  advertiser,
  campaign,
  traffic_sources_costs: rawTrafficSourceCosts,
}: DeepReadonly<AdvertiserUserCampaignResource>): CreatorAdvertiser => {
  const logo = normalizeImage(advertiser.logo)
  const trafficSourceCosts = rawTrafficSourceCosts.map((cost) =>
    normalizeTrafficSourceCost(cost, advertiser.currency),
  )

  return {
    id: advertiser.id,
    name: advertiser.name,
    currency: advertiser.currency,
    logo,
    ...(advertiser.revenue_text && { revenueText: advertiser.revenue_text }),
    ...(campaign && { campaign: normalizeCampaign(campaign, advertiser, trafficSourceCosts) }),
    trafficSourceCosts,
  }
}

export const normalizeCreatorAdvertisers = (
  data: readonly DeepReadonly<AdvertiserUserCampaignResource>[],
): CreatorAdvertiser[] => data.map(normalizeCreatorAdvertiser)
